// @flow
import React from 'react';
import type { Node } from 'react';
import classnames from 'classnames';
import ReactDOM from 'react-dom';
import { noteModalOpening, playExit } from '@helpers/sheetMotion';
import './Modal.scss';

type Props = {
  children: Node,
  className?: string,
  id?: string,
  preventCloseOnOverlayClick?: boolean,
  onClose?: Function,
  ignoreEscape?: boolean,
  throughPortal?: boolean,
  onEscape?: Function,
  onKeyDown?: Function,
};


export default class Modal extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    this.bindPortal();
    noteModalOpening();
  }
  componentWillMount() {
    document.addEventListener('keydown', this.handleKeyDown, true);
    document.addEventListener('keyup', this.handleKeyClose);
    this.bindPortal();
  }

  // In the apps a modal opens with the keyboard closed: it blurs what had focus outside it (the field or
  // search that opened it). A field inside it that focused itself stays focused: children mount first, and
  // in the apps only a field the person just asked for focuses (mobileAutofocus, see shouldAutofocus).
  componentDidMount() {
    const active = document.activeElement;
    if (window.isCordovaApp && active && active !== document.body && !(this.node && this.node.contains(active))) {
      active.blur();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown, true);
    document.removeEventListener('keyup', this.handleKeyClose);
    // The phone: a sheet slides out instead of vanishing (a copy plays it; helpers/sheetMotion)
    playExit(this.node);
  }

  render() {
    const {
      children,
      className,
      id,
      throughPortal,
      onKeyDown,
    } = this.props;

    const cssClasses = classnames(['modal', className]);

    const modal = (
      <div id={id} className={cssClasses} onKeyDown={onKeyDown || (() => { })} ref={this.setNode}>
        <div className="modalOverlay" onClick={this.handleOverlayClick} />
        <div className="modalLayout">
          <div className="modalClose" onClick={this.handleCloseButtonClick} />
          {children}
        </div>
      </div>
    );

    return throughPortal ? ReactDOM.createPortal(modal, this.portalModalContainer) : modal;
  }

  portalModalContainer: any = null;

  node: ?HTMLElement = null;

  setNode = (el: ?HTMLElement) => { this.node = el; };

  handleOverlayClick = () => {
    const { preventCloseOnOverlayClick, onClose } = this.props;
    if (!preventCloseOnOverlayClick && onClose) {
      onClose();
    }
  };

  handleCloseButtonClick = () => {
    const { onClose } = this.props;
    if (onClose) {
      onClose();
    }
  };

  bindPortal = () => {
    let el: any = document.getElementById('portalModalContainer');
    if (!el) {
      el = document.createElement('div');
      el.setAttribute('id', 'portalModalContainer');
      if (document.body) {
        document.body.appendChild(el);
      }
    }
    this.portalModalContainer = el;
  };

  // A SweetAlert dialog on top closes on Escape's keydown but leaves the page only after its 0.15 s fade:
  // an Escape that started on the dialog must not close this modal on keyup ("Não" to Excluir closed the form)
  escapeOnDialog: boolean = false;

  handleKeyDown = (e: Object) => {
    if (e.code === 'Escape') this.escapeOnDialog = this.dialogOpen();
  };

  dialogOpen = () => !!document.body && document.body.getElementsByClassName('swal2-container').length > 0;

  handleKeyClose = (e: Object) => {
    if (e.code !== 'Escape') return;
    const startedOnDialog = this.escapeOnDialog;
    this.escapeOnDialog = false;
    if (!startedOnDialog && !this.dialogOpen()) {
      if (this.props.onClose && !this.props.ignoreEscape) {
        this.props.onClose();
      }
      if (this.props.onEscape) {
        this.props.onEscape();
      }
    }
  };
}
